import React, { useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row, Card, CardBody, CardHeader } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { Breadcrumbs } from '../../../AbstractElements';
import { getDashboardData } from '../../../store/dashboardSlice';

const Social = () => {
  const storeVar = useSelector(state => state.dashboard?.dashboardData) || {};
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDashboardData());
  }, []);

  const getMonthName = (monthNum) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months[monthNum - 1] || "";
  };

  // 1. Process Revenue Chart Data
  const revenueData = storeVar.monthlyRevenue || [];
  const revenueSeries = [{
    name: 'Revenue (₹)',
    data: revenueData.map(item => item.revenue)
  }];
  const revenueCategories = revenueData.map(item => `${getMonthName(item._id.month)} ${item._id.year}`);

  const revenueOptions = {
    chart: { type: 'area', toolbar: { show: false } },
    colors: ['#4e73df'],
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 }
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: { categories: revenueCategories.length ? revenueCategories : ['No Data'] },
    yaxis: { labels: { formatter: (val) => `₹${val.toFixed(0)}` } },
    tooltip: { y: { formatter: (val) => `₹${val.toFixed(2)}` } }
  };

  // 2. Process Registration Chart Data
  const regData = storeVar.monthlyRegistrations || [];
  const regSeries = [{
    name: 'New Users',
    data: regData.map(item => item.count)
  }];
  const regCategories = regData.map(item => `${getMonthName(item._id.month)} ${item._id.year}`);

  const regOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    colors: ['#1cc88a'],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    xaxis: { categories: regCategories.length ? regCategories : ['No Data'] },
  };

  // 3. Process Content Breakdown Pie Chart Data
  const contentSeries = [
    storeVar.testSeries || 0,
    storeVar.mockTest || 0,
    storeVar.pyq || 0,
    storeVar.recordedClass || 0,
    storeVar.liveClass || 0,
    storeVar.eNote || 0
  ];

  const contentOptions = {
    chart: { type: 'donut' },
    labels: ['Exams', 'Mock Tests', 'Previous Year Papers', 'Recorded Subjects', 'Live Subjects', 'ENotes'],
    colors: ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#858796'],
    legend: { position: 'bottom' },
    responsive: [{
      breakpoint: 480,
      options: { chart: { width: 200 }, legend: { position: 'bottom' } }
    }]
  };

  // 4. Process Engagement Radar Chart Data
  const engagementSeries = [{
    name: 'Engagement Count',
    data: [
      storeVar.teachers || 0,
      storeVar.orders || 0,
      storeVar.users || 0,
      storeVar.category || 0
    ]
  }];

  const engagementOptions = {
    chart: { type: 'radar', toolbar: { show: false } },
    colors: ['#e74a3b'],
    labels: ['Teachers', 'Successful Orders', 'Total Users', 'Exam Series'],
    stroke: { width: 2 },
    fill: { opacity: 0.2 },
    markers: { size: 4 },
    xaxis: {
      labels: {
        style: { colors: '#2c3e50', fontSize: '11px', fontWeight: 500 }
      }
    }
  };

  // Modern Card Styles
  const cardStyle = (gradient, shadow) => ({
    background: gradient,
    borderRadius: '16px',
    border: 'none',
    boxShadow: shadow,
    color: '#fff',
    transition: 'all 0.3s ease-in-out',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
  });

  const cardIconStyle = {
    fontSize: '32px',
    opacity: 0.85,
  };

  const widgets = [
    { title: 'Exam Series', total: storeVar.category || 0, icon: '📚', gradient: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', shadow: '0 8px 20px rgba(79, 70, 229, 0.25)' },
    { title: 'Exams Available', total: storeVar.testSeries || 0, icon: '🖊️', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', shadow: '0 8px 20px rgba(6, 182, 212, 0.25)' },
    { title: 'Mock Tests', total: storeVar.mockTest || 0, icon: '📝', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', shadow: '0 8px 20px rgba(16, 185, 129, 0.25)' },
    { title: 'Previous Year Papers', total: storeVar.pyq || 0, icon: '🗄️', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', shadow: '0 8px 20px rgba(139, 92, 246, 0.25)' },
    { title: 'Recorded Classes', total: storeVar.recordedClass || 0, icon: '🎥', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', shadow: '0 8px 20px rgba(245, 158, 11, 0.25)' },
    { title: 'Live Classes', total: storeVar.liveClass || 0, icon: '📡', gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', shadow: '0 8px 20px rgba(236, 72, 153, 0.25)' },
    { title: 'ENotes Subject', total: storeVar.eNote || 0, icon: '📖', gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)', shadow: '0 8px 20px rgba(20, 184, 166, 0.25)' },
    { title: 'Active Teachers', total: storeVar.teachers || 0, icon: '👨‍🏫', gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', shadow: '0 8px 20px rgba(99, 102, 241, 0.25)' },
    { title: 'Total Users', total: storeVar.users || 0, icon: '👥', gradient: 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)', shadow: '0 8px 20px rgba(244, 63, 94, 0.25)' },
    { title: 'Completed Orders', total: storeVar.orders || 0, icon: '🛒', gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', shadow: '0 8px 20px rgba(34, 197, 94, 0.25)' },
  ];

  return (
    <Fragment>
      <Breadcrumbs mainTitle='Dashboard Overview' parent='Dashboard' title='' />
      <Container fluid={true}>
        {/* Modern Custom Metric Grid with smaller gaps */}
        <Row className="g-3 mb-4">
          {widgets.map((widget, idx) => (
            <Col sm='6' xl='3' lg='4' key={idx}>
              <Card 
                style={cardStyle(widget.gradient, widget.shadow)} 
                className="dashboard-metric-card border-0"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = widget.shadow.replace('0.25', '0.4');
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = widget.shadow;
                }}
              >
                {/* Decorative background circle */}
                <div style={{
                  position: 'absolute',
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  top: '-40px',
                  right: '-20px',
                  pointerEvents: 'none'
                }} />
                
                <CardBody className="d-flex align-items-center justify-content-between p-4">
                  <div>
                    <h3 className="m-0 f-w-800 f-28" style={{ lineHeight: 1.1 }}>{widget.total}</h3>
                    <p className="m-0 mt-1 f-w-500 f-13" style={{ opacity: 0.85, letterSpacing: '0.3px' }}>{widget.title}</p>
                  </div>
                  <div style={cardIconStyle}>{widget.icon}</div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="mt-4">
          {/* Sales Revenue Monthly Chart */}
          <Col xl='6' lg='12' className='mb-4'>
            <Card className="border-0" style={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardHeader className="d-flex align-items-center justify-content-between pb-0" style={{ borderBottom: 'none', background: 'transparent', padding: '24px' }}>
                <h5 className="f-w-700 f-16 m-0" style={{ color: '#2c3e50' }}>Sales Revenue Trend (Last 6 Months)</h5>
              </CardHeader>
              <CardBody style={{ padding: '0 24px 24px 24px' }}>
                {revenueData.length > 0 ? (
                  <ReactApexChart options={revenueOptions} series={revenueSeries} type="area" height={320} />
                ) : (
                  <div className="text-center py-5 text-muted">No sales revenue recorded in this period.</div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* User Signup Growth Chart */}
          <Col xl='6' lg='12' className='mb-4'>
            <Card className="border-0" style={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardHeader className="d-flex align-items-center justify-content-between pb-0" style={{ borderBottom: 'none', background: 'transparent', padding: '24px' }}>
                <h5 className="f-w-700 f-16 m-0" style={{ color: '#2c3e50' }}>User Registrations (Last 6 Months)</h5>
              </CardHeader>
              <CardBody style={{ padding: '0 24px 24px 24px' }}>
                {regData.length > 0 ? (
                  <ReactApexChart options={regOptions} series={regSeries} type="bar" height={320} />
                ) : (
                  <div className="text-center py-5 text-muted">No new user registrations recorded.</div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-2">
          {/* Content Breakdown Distribution Chart */}
          <Col xl='6' lg='12' className='mb-4'>
            <Card className="border-0" style={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardHeader className="text-center pb-0" style={{ borderBottom: 'none', background: 'transparent', padding: '24px' }}>
                <h5 className="f-w-700 f-16 m-0" style={{ color: '#2c3e50' }}>Course Content Breakdown</h5>
              </CardHeader>
              <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: 320, padding: '0 24px 24px 24px' }}>
                <ReactApexChart options={contentOptions} series={contentSeries} type="donut" width={420} />
              </CardBody>
            </Card>
          </Col>

          {/* User & Sales Engagement Radar Chart */}
          <Col xl='6' lg='12' className='mb-4'>
            <Card className="border-0" style={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardHeader className="text-center pb-0" style={{ borderBottom: 'none', background: 'transparent', padding: '24px' }}>
                <h5 className="f-w-700 f-16 m-0" style={{ color: '#2c3e50' }}>User Engagement Breakdown</h5>
              </CardHeader>
              <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: 320, padding: '0 24px 24px 24px' }}>
                <ReactApexChart options={engagementOptions} series={engagementSeries} type="radar" height={300} width={420} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default Social;
